import { IFixBay40sLocationResult, fixBay40sLocation } from "./fixBay40sLocation";
import { IFixBayLevelStructureResult, fixBayLevelStructure } from "./fixBayLevelStructure";
import { IFixCenterLineRowResult, fixCenterLineRow } from "./fixCenterLineRow";
import { IFixRowWeightsResult, fixRowWeights } from "./fixRowWeights";
import { IFixSlotStructureResult, fixSlotStructure } from "./fixSlotStructure";

import { IOpenVesselDefinitionV1 } from "open-vessel-definition";

export type TRepairStepName =
  "bayLevelStructure" | "slotStructure" | "rowWeights" | "centerLineRow" | "bay40s";

/** One step of `repairOvd`, for generic reporting */
export interface IRepairStep {
  name: TRepairStepName;
  changed: boolean;
  /** How many things the step fixed */
  count: number;
  /** A silent step changes the file without being worth a dialog on its own */
  silent: boolean;
}

export interface IRepairOvdResult {
  /** The repaired vessel. The input object when nothing changed */
  json: IOpenVesselDefinitionV1;
  bayLevelStructure: IFixBayLevelStructureResult;
  slotStructure: IFixSlotStructureResult;
  rowWeights: IFixRowWeightsResult;
  centerLineRow: IFixCenterLineRowResult;
  bay40s: IFixBay40sLocationResult;
  /** The steps in the order they ran */
  steps: IRepairStep[];
  changed: boolean;
  /** Some non-silent step changed the file: the user should be told */
  notable: boolean;
}

/**
 * The repairs applied to a vessel as it opens, in order:
 *
 * 1. `fixBayLevelStructure`: `size`/`isoRow` set to their keys, STAF leftovers removed;
 * 2. `fixSlotStructure`: `pos` set to its key, empty slots made `restricted`, STAF leftovers removed;
 * 3. `fixRowWeights`: placeholder weights dropped, so they never make a pair look split;
 * 4. `fixCenterLineRow`: row 00 slots in Bay-levels without a centerline row;
 * 5. `fixBay40sLocation`: 40'+ definitions in the wrong Bay of their pair.
 *
 * Each step takes the previous step's output, leaves data it cannot draw out of the way
 * without losing what can be kept, and reports what it did so the user can be told. The
 * input is not modified. Every step is deterministic and lossless; anything else stays a
 * validator finding.
 */
export function repairOvd(json: IOpenVesselDefinitionV1): IRepairOvdResult {
  const bayLevelStructure = fixBayLevelStructure(json);
  const slotStructure = fixSlotStructure(bayLevelStructure.json);
  const rowWeights = fixRowWeights(slotStructure.json);
  const centerLineRow = fixCenterLineRow(rowWeights.json);
  const bay40s = fixBay40sLocation(centerLineRow.json);

  const steps: IRepairStep[] = [
    {
      name: "bayLevelStructure",
      changed: bayLevelStructure.changed,
      count: bayLevelStructure.fixed.length,
      silent: false,
    },
    {
      name: "slotStructure",
      changed: slotStructure.changed,
      count: slotStructure.fixed.length,
      silent: false,
    },
    {
      name: "rowWeights",
      changed: rowWeights.changed,
      count: rowWeights.dropped.length,
      silent: false,
    },
    {
      name: "centerLineRow",
      changed: centerLineRow.changed,
      count: centerLineRow.repaired.length,
      silent: false,
    },
    { name: "bay40s", changed: bay40s.changed, count: bay40s.moved.length, silent: false },
  ];

  return {
    json: bay40s.json,
    bayLevelStructure,
    slotStructure,
    rowWeights,
    centerLineRow,
    bay40s,
    steps,
    changed: steps.some((s) => s.changed),
    notable: steps.some((s) => s.changed && !s.silent),
  };
}
